import React from 'react';
import {
  AddLine,
  DeleteLine,
  FlagLine,
  LinkLine,
  OpenLine,
} from '@dropbox/dig-icons/assets';
import { UIIcon } from '@dropbox/dig-icons';
import { Checkbox } from '@dropbox/dig-components/controls';
import { checkFeatureFlag } from 'js/sign-components/common/check-feature-flag';
import {
  FeatureFlags,
  isExperimentFeatureFlagName,
  REGISTERED_BOOLEAN_FEATURE_FLAG_NAMES,
} from 'js/sign-components/common/feature-flags';
import { useFeatureFlags } from 'js/sign-components/common/feature-flag';
import { DebugPanel, PanelComponent } from './debug-panel';
import {
  applyFeatureFlagOverrides,
  getRequestFlagNames,
  getRequestFlagValue,
  GrowthbookRequestFlags,
  isFlagCheckedThisRequest,
  normalizeFeatureFlagName,
  parseFeatureFlagOverrideCookies,
  PendingFeatureFlagOverride,
  readGrowthbookRequestFlags,
} from './feature-flag-overrides';
import styles from './feature-flag-debug-panel.module.scss';

type OverrideValue = 'on' | 'off';

type FlagEntry = {
  id: string;
  name: string;
  enabled: boolean;
  value: OverrideValue;
  isManual: boolean;
};

type FlagRowMeta = {
  name: string;
  pageValue: boolean | undefined;
  cookieValue: boolean | undefined;
  requestValue: boolean | undefined;
  checkedThisRequest: boolean;
  isExperimentFlag: boolean;
};

function findFeatureFlagKey(
  name: string,
  featureFlags: FeatureFlags,
): keyof FeatureFlags | undefined {
  const normalizedName = normalizeFeatureFlagName(name);
  return Object.keys(featureFlags).find(
    (key) => key.toLowerCase() === normalizedName,
  ) as keyof FeatureFlags | undefined;
}

function getPageFlagValue(
  name: string,
  featureFlags: FeatureFlags,
): boolean | undefined {
  const matchedKey = findFeatureFlagKey(name, featureFlags);
  if (!matchedKey) {
    return undefined;
  }

  return checkFeatureFlag(matchedKey, featureFlags);
}

function buildFlagRows(
  featureFlags: FeatureFlags,
  cookieOverrides: Record<string, boolean>,
  manualFlagNames: string[],
  requestFlags: GrowthbookRequestFlags,
): FlagRowMeta[] {
  const names = new Set<string>([
    ...getRequestFlagNames(requestFlags),
    ...REGISTERED_BOOLEAN_FEATURE_FLAG_NAMES,
    ...Object.keys(featureFlags),
    ...Object.keys(cookieOverrides),
    ...manualFlagNames.map(normalizeFeatureFlagName),
  ]);

  return [...names]
    .sort((a, b) => {
      const aRequest = isFlagCheckedThisRequest(a, requestFlags);
      const bRequest = isFlagCheckedThisRequest(b, requestFlags);
      if (aRequest !== bRequest) {
        return aRequest ? -1 : 1;
      }

      const aCookie =
        cookieOverrides[normalizeFeatureFlagName(a)] !== undefined;
      const bCookie =
        cookieOverrides[normalizeFeatureFlagName(b)] !== undefined;
      if (aCookie !== bCookie) {
        return aCookie ? -1 : 1;
      }

      const aOnPage = findFeatureFlagKey(a, featureFlags) !== undefined;
      const bOnPage = findFeatureFlagKey(b, featureFlags) !== undefined;
      if (aOnPage !== bOnPage) {
        return aOnPage ? -1 : 1;
      }

      return a.localeCompare(b);
    })
    .map((name) => ({
      name,
      pageValue: getPageFlagValue(name, featureFlags),
      cookieValue: cookieOverrides[normalizeFeatureFlagName(name)],
      requestValue: getRequestFlagValue(name, requestFlags),
      checkedThisRequest: isFlagCheckedThisRequest(name, requestFlags),
      isExperimentFlag: isExperimentFeatureFlagName(name),
    }));
}

function rowMetaToEntry(row: FlagRowMeta): FlagEntry {
  if (row.isExperimentFlag) {
    return {
      id: normalizeFeatureFlagName(row.name),
      name: row.name,
      enabled: false,
      value: row.requestValue === false ? 'off' : 'on',
      isManual: false,
    };
  }

  if (row.cookieValue !== undefined) {
    return {
      id: normalizeFeatureFlagName(row.name),
      name: row.name,
      enabled: true,
      value: row.cookieValue ? 'on' : 'off',
      isManual: false,
    };
  }

  return {
    id: normalizeFeatureFlagName(row.name),
    name: row.name,
    enabled: false,
    value: row.requestValue === false ? 'off' : 'on',
    isManual: false,
  };
}

function buildEntriesFromRows(rows: FlagRowMeta[]): FlagEntry[] {
  return rows.map(rowMetaToEntry);
}

function RocketIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M12 2.5c-.4 0-.8.2-1 .6L8.2 8.5 2.8 9.8c-.5.1-.9.5-1 1-.1.5.1 1 .5 1.3l4.2 3.1-1.4 5.2c-.1.5.1 1 .5 1.3.4.3 1 .3 1.4 0L12 18.8l5 3.9c.4.3 1 .3 1.4 0 .4-.3.6-.8.5-1.3l-1.4-5.2 4.2-3.1c.4-.3.6-.8.5-1.3-.1-.5-.5-.9-1-1l-5.4-1.3-2.8-5.4c-.2-.4-.6-.6-1-.6zm0 3.8 1.8 3.5.1.2 5 .9-3.6 2.7-.2.1-1.1 4.1-1.1-4.1-.1-.2-3.6-2.7 5-.9.2-.1 1.8-3.5z" />
    </svg>
  );
}

function getGrowthbookFeatureUrl(flagName: string): string {
  return `https://app.dropboxexperiment.com/features/${encodeURIComponent(flagName.toLowerCase())}`;
}

function entriesToPendingOverrides(
  entries: FlagEntry[],
  cookieOverrides: Record<string, boolean>,
): Record<string, PendingFeatureFlagOverride> {
  const pending: Record<string, PendingFeatureFlagOverride> = {};

  entries.forEach((entry) => {
    const normalizedName = normalizeFeatureFlagName(entry.name);
    if (!normalizedName || isExperimentFeatureFlagName(normalizedName)) {
      return;
    }

    if (!entry.enabled) {
      if (cookieOverrides[normalizedName] !== undefined) {
        pending[normalizedName] = 'clear';
      }
      return;
    }

    pending[normalizedName] = entry.value === 'on';
  });

  return pending;
}

function filterEntriesForView(
  entries: FlagEntry[],
  rows: FlagRowMeta[],
  viewMode: 'request' | 'all',
  query: string,
): FlagEntry[] {
  const normalizedQuery = query.trim().toLowerCase();

  return entries
    .filter((entry) => {
      const rowMeta = rows.find(
        (row) =>
          normalizeFeatureFlagName(row.name) ===
          normalizeFeatureFlagName(entry.name),
      );

      if (
        viewMode === 'request' &&
        !entry.isManual &&
        !rowMeta?.checkedThisRequest &&
        rowMeta?.cookieValue === undefined
      ) {
        return false;
      }

      if (!normalizedQuery) {
        return true;
      }

      return entry.name.toLowerCase().includes(normalizedQuery);
    })
    .sort((a, b) => {
      if (a.isManual !== b.isManual) {
        return a.isManual ? -1 : 1;
      }

      if (a.isManual && b.isManual) {
        if (!a.name.trim() && b.name.trim()) {
          return -1;
        }
        if (a.name.trim() && !b.name.trim()) {
          return 1;
        }
      }

      return 0;
    });
}

function countLaunchableOverrides(
  entries: FlagEntry[],
  cookieOverrides: Record<string, boolean>,
): number {
  return entries.filter((entry) => {
    if (!entry.name.trim()) {
      return false;
    }

    if (isExperimentFeatureFlagName(entry.name)) {
      return false;
    }

    if (entry.enabled) {
      return true;
    }

    return cookieOverrides[normalizeFeatureFlagName(entry.name)] !== undefined;
  }).length;
}

const FeatureFlagPanel: PanelComponent = ({ isOpen }) => {
  const featureFlags = useFeatureFlags();
  const [activeTab, setActiveTab] = React.useState<'main' | 'settings'>('main');
  const [viewMode, setViewMode] = React.useState<'request' | 'all'>('request');
  const [requestFlags, setRequestFlags] =
    React.useState<GrowthbookRequestFlags>(() => readGrowthbookRequestFlags());
  const [cookieOverrides, setCookieOverrides] = React.useState(() =>
    parseFeatureFlagOverrideCookies(),
  );
  const [manualFlagNames, setManualFlagNames] = React.useState<string[]>([]);
  const [entries, setEntries] = React.useState<FlagEntry[]>([]);
  const [filter, setFilter] = React.useState('');
  const flagListRef = React.useRef<HTMLDivElement>(null);
  const newEntryInputRef = React.useRef<HTMLInputElement>(null);
  const nextEntryIdRef = React.useRef(0);
  const [focusedEntryId, setFocusedEntryId] = React.useState<string | null>(
    null,
  );

  const rows = React.useMemo(
    () =>
      buildFlagRows(
        featureFlags,
        cookieOverrides,
        manualFlagNames,
        requestFlags,
      ),
    [cookieOverrides, featureFlags, manualFlagNames, requestFlags],
  );

  const wasOpenRef = React.useRef(false);

  React.useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      const overrides = parseFeatureFlagOverrideCookies();
      const requestFlagData = readGrowthbookRequestFlags();
      setRequestFlags(requestFlagData);
      setCookieOverrides(overrides);
      setEntries(
        buildEntriesFromRows(
          buildFlagRows(
            featureFlags,
            overrides,
            manualFlagNames,
            requestFlagData,
          ),
        ),
      );
    }
    wasOpenRef.current = isOpen;
  }, [featureFlags, isOpen, manualFlagNames]);

  const filteredEntries = React.useMemo(
    () => filterEntriesForView(entries, rows, viewMode, filter),
    [entries, filter, rows, viewMode],
  );

  const requestEntryCount = React.useMemo(
    () => filterEntriesForView(entries, rows, 'request', '').length,
    [entries, rows],
  );

  const allEntryCount = React.useMemo(
    () => filterEntriesForView(entries, rows, 'all', '').length,
    [entries, rows],
  );

  const setViewModeAndScroll = (mode: 'request' | 'all') => {
    setViewMode(mode);
    window.requestAnimationFrame(() => {
      flagListRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    });
  };

  const launchCount = countLaunchableOverrides(entries, cookieOverrides);

  const updateEntry = (index: number, patch: Partial<FlagEntry>) => {
    setEntries((current) =>
      current.map((entry, entryIndex) =>
        entryIndex === index ? { ...entry, ...patch } : entry,
      ),
    );
  };

  const addFeature = () => {
    const entryId = `new-${nextEntryIdRef.current++}`;
    setFocusedEntryId(entryId);
    setEntries((current) => [
      {
        id: entryId,
        name: '',
        enabled: true,
        value: 'on',
        isManual: true,
      },
      ...current,
    ]);
    window.requestAnimationFrame(() => {
      flagListRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
      newEntryInputRef.current?.focus();
    });
  };

  const removeEntry = (index: number) => {
    setEntries((current) => {
      const removed = current[index];
      if (removed?.isManual) {
        const normalized = normalizeFeatureFlagName(removed.name);
        setManualFlagNames((names) =>
          names.filter((name) => name !== normalized),
        );
      }
      return current.filter((_, entryIndex) => entryIndex !== index);
    });
  };

  const syncManualFlagName = (index: number, name: string) => {
    const normalized = normalizeFeatureFlagName(name);
    setEntries((current) =>
      current.map((entry, entryIndex) =>
        entryIndex === index ? { ...entry, name: normalized || name } : entry,
      ),
    );
    if (normalized) {
      setManualFlagNames((current) =>
        current.includes(normalized) ? current : [...current, normalized],
      );
    }
  };

  const launchOverrides = () => {
    if (launchCount === 0) {
      return;
    }
    applyFeatureFlagOverrides(
      entriesToPendingOverrides(entries, cookieOverrides),
    );
  };

  return (
    <div className={`${styles.panel} foundation-exempt`}>
      <div className={styles.header}>
        <h2 className={styles.title}>Growthbook Overrides</h2>
      </div>

      <div className={styles.tabs}>
        <button
          type="button"
          className={activeTab === 'main' ? styles.tabActive : styles.tab}
          onClick={() => setActiveTab('main')}
        >
          Main
        </button>
        <button
          type="button"
          className={activeTab === 'settings' ? styles.tabActive : styles.tab}
          onClick={() => setActiveTab('settings')}
        >
          Settings
        </button>
      </div>

      {activeTab === 'main' ? (
        <div className={styles.tabPanel}>
          <div className={styles.toolbar}>
            <div className={styles.toolbarRow}>
              <button
                type="button"
                className={styles.toolbarButton}
                onClick={addFeature}
              >
                <UIIcon width={16} height={16} src={AddLine} />
                Add Feature
              </button>
              <select
                id="feature-flag-view-mode"
                className={styles.toolbarSelect}
                value={viewMode}
                aria-label="Flag list scope"
                onChange={(event) =>
                  setViewModeAndScroll(event.target.value as 'request' | 'all')
                }
              >
                <option value="request">
                  {`This request (${requestEntryCount})`}
                </option>
                <option value="all">{`All flags (${allEntryCount})`}</option>
              </select>
              <span className={styles.toolbarSpacer} />
              <button
                type="button"
                className={styles.launchButton}
                disabled={launchCount === 0}
                onClick={launchOverrides}
              >
                <RocketIcon />
                Override current page!
              </button>
            </div>
          </div>

          <input
            type="search"
            className={styles.searchInput}
            value={filter}
            onChange={(event) => setFilter(event.target.value)}
            placeholder="Filter flags"
          />

          <div className={styles.flagList} ref={flagListRef}>
            {filteredEntries.length === 0 ? (
              <div className={styles.emptyState}>
                {viewMode === 'request'
                  ? 'No flags were checked during this request yet.'
                  : 'No flags match your filter.'}
              </div>
            ) : (
              filteredEntries.map((entry) => {
                const index = entries.findIndex((item) => item.id === entry.id);
                const rowMeta = rows.find(
                  (row) =>
                    normalizeFeatureFlagName(row.name) ===
                    normalizeFeatureFlagName(entry.name),
                );
                const isPending =
                  entry.enabled ||
                  (rowMeta?.cookieValue !== undefined && !entry.enabled);
                const isNewManualEntry =
                  entry.isManual && entry.id === focusedEntryId;
                const isExperimentFlag =
                  rowMeta?.isExperimentFlag ??
                  isExperimentFeatureFlagName(entry.name);

                return (
                  <div
                    key={entry.id}
                    className={[
                      styles.flagRow,
                      isPending ? styles.flagRowPending : '',
                      isNewManualEntry ? styles.flagRowNew : '',
                      isExperimentFlag ? styles.flagRowExperiment : '',
                      !entry.enabled ? styles.flagRowDisabled : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  >
                    <div className={styles.checkboxCell}>
                      <Checkbox
                        checked={entry.enabled}
                        disabled={isExperimentFlag}
                        onChange={(event) =>
                          updateEntry(index, { enabled: event.target.checked })
                        }
                        aria-label={`Enable override for ${entry.name || 'new flag'}`}
                      />
                    </div>
                    <input
                      type="text"
                      ref={isNewManualEntry ? newEntryInputRef : undefined}
                      className={[
                        styles.flagInput,
                        entry.isManual ? '' : styles.flagInputReadOnly,
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      value={entry.name}
                      readOnly={!entry.isManual}
                      placeholder="sign_core_2026_08_25_my_flag"
                      onChange={(event) =>
                        syncManualFlagName(index, event.target.value)
                      }
                    />
                    <select
                      className={styles.valueSelect}
                      value={entry.value}
                      disabled={!entry.enabled || isExperimentFlag}
                      onChange={(event) =>
                        updateEntry(index, {
                          value: event.target.value as OverrideValue,
                        })
                      }
                    >
                      <option value="on">ON</option>
                      <option value="off">OFF</option>
                    </select>
                    <div className={styles.rowActions}>
                      {entry.name && (
                        <>
                          <a
                            href={getGrowthbookFeatureUrl(entry.name)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={styles.iconButton}
                            title="Open in Growthbook"
                            aria-label={`Open ${entry.name} in Growthbook`}
                          >
                            <UIIcon width={18} height={18} src={OpenLine} />
                          </a>
                          <button
                            type="button"
                            className={styles.iconButton}
                            title="Copy flag name"
                            aria-label={`Copy ${entry.name}`}
                            onClick={() =>
                              navigator.clipboard.writeText(entry.name)
                            }
                          >
                            <UIIcon width={18} height={18} src={LinkLine} />
                          </button>
                        </>
                      )}
                      <button
                        type="button"
                        className={styles.iconButton}
                        title="Remove flag"
                        aria-label={`Remove ${entry.name || 'flag'}`}
                        onClick={() => removeEntry(index)}
                      >
                        <UIIcon width={18} height={18} src={DeleteLine} />
                      </button>
                    </div>
                    {rowMeta && (
                      <div className={styles.pageHint}>
                        {isExperimentFlag && (
                          <>
                            Experiment flag · cookie override not supported
                            {' · '}
                          </>
                        )}
                        {rowMeta.requestValue !== undefined && (
                          <>
                            Checked this request:{' '}
                            <span
                              className={
                                rowMeta.requestValue
                                  ? styles.pageHintOn
                                  : styles.pageHintOff
                              }
                            >
                              {rowMeta.requestValue ? 'ON' : 'OFF'}
                            </span>
                            {' · '}
                          </>
                        )}
                        Page value:{' '}
                        {rowMeta.pageValue === undefined ? (
                          <span className={styles.pageHintOff}>
                            not sent to this page
                          </span>
                        ) : (
                          <span
                            className={
                              rowMeta.pageValue
                                ? styles.pageHintOn
                                : styles.pageHintOff
                            }
                          >
                            {rowMeta.pageValue ? 'ON' : 'OFF'}
                          </span>
                        )}
                        {rowMeta.cookieValue !== undefined && (
                          <>
                            {' '}
                            · Cookie override:{' '}
                            {rowMeta.cookieValue ? 'ON' : 'OFF'}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      ) : (
        <div className={`${styles.tabPanel} ${styles.settingsSection}`}>
          <p>
            Checked flags with ON/OFF are written as{' '}
            <code>ff_&#123;flag_name&#125;</code> cookies and applied after you
            click &quot;Override current page!&quot;.
          </p>
          <ul className={styles.settingsList}>
            <li>
              Check a flag and choose ON or OFF, then launch. Uncheck a flag
              that has a cookie override to clear it on launch.
            </li>
            <li>
              Experiment flags (for example trial-user auto-placement) are shown
              read-only because they are resolved through Growthbook
              experiments, not boolean cookie overrides.
            </li>
            <li>Overrides only work in non-production environments.</li>
            <li>
              For persistent local dev defaults, use{' '}
              <code>.growthbook_overrides.json</code> at the repo root.
            </li>
          </ul>
        </div>
      )}
    </div>
  );
};

export const featureFlagDebugPanel = NODE_ENV !== 'production' && (
  <DebugPanel
    Component={FeatureFlagPanel}
    icon={FlagLine}
    title="Growthbook overrides"
    updateWhileClosed={false}
  />
);
